import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Leaf, Snowflake, Sun, Flower2, CheckCircle2, Sparkles } from 'lucide-react';
import { Card } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { Badge } from './ui/badge';
import { logError } from '../lib/logger';
import { VIEWPORT_ONCE } from '../lib/motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const SEASON_ICONS = { printemps: Flower2, ete: Sun, automne: Leaf, hiver: Snowflake };
const REFRESH_WHEN_PENDING_MS = 45000;

/**
 * Seasonal, city-specific editorial block (local SEO). Shows the AI-written text when it exists,
 * otherwise an instant template-based text; transparently upgrades when the AI version is ready.
 * pageType: 'general' | 'menage' | 'jardinage' | 'taille-haies'
 */
export const SeasonalLocalContent = ({ pageType, citySlug, cityName }) => {
  const [content, setContent] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer;
    const load = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/city-content/${pageType}/${citySlug}`);
        if (cancelled) return;
        setContent(res.data);
        setFailed(false);
        // The AI version is being written server-side: refresh once to pick it up
        if (res.data.pending && res.data.source !== 'ai') timer = setTimeout(load, REFRESH_WHEN_PENDING_MS);
      } catch (err) {
        if (cancelled) return;
        logError('contenu local saisonnier', err);
        setFailed(true);
      }
    };
    setContent(null);
    load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [pageType, citySlug]);

  if (failed) return null;

  if (!content) {
    return (
      <section className="py-12 sm:py-16" data-testid="seasonal-content-loading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-3">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </section>
    );
  }

  const Icon = SEASON_ICONS[content.season] || Leaf;

  return (
    <section className="py-12 sm:py-16 lg:py-20" data-testid="seasonal-content" data-source={content.source} data-season={content.season}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEWPORT_ONCE} transition={{ duration: 0.5 }}>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Badge className="bg-[hsl(var(--brand-mint))] text-foreground hover:bg-[hsl(var(--brand-mint))]" data-testid="seasonal-content-badge">
              <Icon className="h-3.5 w-3.5 mr-1.5 text-[hsl(var(--primary))]" /> Conseils de saison — {content.season_label}
            </Badge>
            {(content.source === 'ai' || content.source === 'ai-template') && (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="seasonal-content-ai-note"><Sparkles className="h-3 w-3" /> Rédigé pour {cityName || content.city}</span>
            )}
          </div>
          <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-6" data-testid="seasonal-content-title">{content.titre}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8">
            <div className="space-y-4" data-testid="seasonal-content-paragraphs">
              {content.paragraphes.map((p, i) => (
                <p key={`${content.season}-${i}-${p.slice(0, 20)}`} className="text-muted-foreground leading-relaxed">{p}</p>
              ))}
            </div>
            {content.conseils?.length > 0 && (
              <Card className="p-5 h-fit bg-[hsl(var(--secondary))]/60 border-border" data-testid="seasonal-content-tips">
                <p className="text-sm font-semibold mb-3 flex items-center gap-2"><Icon className="h-4 w-4 text-[hsl(var(--primary))]" /> À faire {content.season === 'ete' ? 'cet été' : content.season === 'hiver' ? 'cet hiver' : `ce ${content.season_label}`} à {cityName || content.city}</p>
                <ul className="space-y-2.5">
                  {content.conseils.map((tip) => (
                    <li key={tip} className="flex items-start gap-2 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5 flex-shrink-0" /><span>{tip}</span></li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
